const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

const smoothstep = (low, high, value) => {
  const ratio = clamp((value - low) / (high - low || 1), 0, 1);
  return ratio * ratio * (3 - 2 * ratio);
};

const snapIncrement = (value, increment) => {
  const step = Math.max(1, Number(increment || 1));
  return Math.round((Number(value) + Number.EPSILON) / step) * step;
};

export const getAmpBandWeight = ({ seatIndex, seatCount, bandMode = 'middle', bandSoftness = 0.18, bandCore = 0.3 }) => {
  if (seatCount <= 1) return 1;
  const position = seatIndex / (seatCount - 1);
  const softness = clamp(Number(bandSoftness || 0), 0, 0.49);
  if (bandMode === 'left') return softness === 0 ? Number(position <= 0.5) : clamp(1 - smoothstep(0.5 - softness, 0.5 + softness, position), 0, 1);
  if (bandMode === 'right') return softness === 0 ? Number(position >= 0.5) : clamp(smoothstep(0.5 - softness, 0.5 + softness, position), 0, 1);
  const halfCore = clamp(Number(bandCore || 0.3), 0.01, 0.99) / 2;
  const outer = clamp(halfCore + softness, halfCore, 0.5);
  const distance = Math.abs(position - 0.5);
  if (distance <= halfCore) return 1;
  if (distance >= outer) return 0;
  return clamp(1 - smoothstep(halfCore, outer, distance), 0, 1);
};

export const calculateAmpModelPrice = (settings, { rowIndex, rowCount, seatIndex, seatCount }) => {
  let floorPrice = Number(settings?.floor_price || 0);
  let ceilingPrice = Number(settings?.ceiling_price || 0);
  if (!Number.isFinite(floorPrice) || !Number.isFinite(ceilingPrice)) return 0;
  if (ceilingPrice < floorPrice) [floorPrice, ceilingPrice] = [ceilingPrice, floorPrice];
  const safeRows = Math.max(1, Number(rowCount || 1));
  const safeSeats = Math.max(1, Number(seatCount || 1));
  const rowPosition = safeRows === 1 ? 1 : 1 - Number(rowIndex || 0) / (safeRows - 1);
  const rowWeight = clamp(rowPosition, 0, 1) ** Number(settings?.row_exponent || 1.2);
  const bandWeight = getAmpBandWeight({
    seatIndex: Number(seatIndex || 0),
    seatCount: safeSeats,
    bandMode: settings?.band_mode,
    bandSoftness: settings?.band_softness,
    bandCore: settings?.band_core,
  });
  const adjustedFloor = floorPrice + Number(settings?.floor_middle_delta || 0) * bandWeight;
  const edgeStrength = clamp(Number(settings?.edge_strength ?? 0.65), 0, 1);
  const rawPrice = adjustedFloor + rowWeight * (edgeStrength + (1 - edgeStrength) * bandWeight) * (ceilingPrice - adjustedFloor);
  const bucketCount = Math.max(2, Number(settings?.bucket_count || 7));
  const roundTo = Math.max(1, Number(settings?.round_to || 5));
  if (ceilingPrice <= floorPrice) return Math.round(floorPrice);
  const buckets = Array.from({ length: bucketCount }, (_, index) => (
    snapIncrement(floorPrice + index / (bucketCount - 1) * (ceilingPrice - floorPrice), roundTo)
  ));
  const bucketIndex = clamp(Math.round((rawPrice - floorPrice) / (ceilingPrice - floorPrice || 1) * (buckets.length - 1)), 0, buckets.length - 1);
  return Math.round(clamp(buckets[bucketIndex], floorPrice, ceilingPrice));
};

export const fitAmpPricingModelToAnchors = (currentSettings, anchors = []) => {
  const valid = anchors.filter((anchor) => Number.isFinite(Number(anchor.targetPrice)) && Number(anchor.targetPrice) > 0);
  if (!valid.length) return currentSettings;
  const increment = Math.max(1, Number(currentSettings?.round_to || 5));
  const inferredMode = valid.length === 1
    ? (valid[0].seatCount <= 1 ? 'middle' : valid[0].seatIndex / (valid[0].seatCount - 1) < 0.34 ? 'left' : valid[0].seatIndex / (valid[0].seatCount - 1) > 0.66 ? 'right' : 'middle')
    : null;
  const modes = inferredMode ? [inferredMode] : [...new Set([currentSettings?.band_mode || 'middle', 'left', 'middle', 'right'])];
  const rowExponents = [...new Set([Number(currentSettings?.row_exponent || 1.2), 0.75, 1, 1.2, 1.5, 2, 2.5])];
  const edgeStrengths = [...new Set([Number(currentSettings?.edge_strength ?? 0.65), 0.35, 0.5, 0.65, 0.78, 0.9])];
  let best = null;

  modes.forEach((bandMode) => rowExponents.forEach((rowExponent) => edgeStrengths.forEach((edgeStrength) => {
    const shape = { ...currentSettings, band_mode: bandMode, row_exponent: rowExponent, edge_strength: edgeStrength, floor_middle_delta: 0, floor_price: 0, ceiling_price: 100, bucket_count: 101, round_to: 1 };
    const weights = valid.map((anchor) => calculateAmpModelPrice(shape, anchor) / 100);
    let floorPrice;
    let ceilingPrice;
    if (valid.length === 1) {
      const currentPrediction = calculateAmpModelPrice({ ...currentSettings, band_mode: bandMode, row_exponent: rowExponent, edge_strength: edgeStrength }, valid[0]);
      const shift = Number(valid[0].targetPrice) - currentPrediction;
      floorPrice = Number(currentSettings?.floor_price || 0) + shift;
      ceilingPrice = Number(currentSettings?.ceiling_price || 0) + shift;
    } else {
      const meanWeight = weights.reduce((sum, value) => sum + value, 0) / weights.length;
      const meanTarget = valid.reduce((sum, anchor) => sum + Number(anchor.targetPrice), 0) / valid.length;
      const variance = weights.reduce((sum, value) => sum + (value - meanWeight) ** 2, 0);
      const covariance = weights.reduce((sum, value, index) => sum + (value - meanWeight) * (Number(valid[index].targetPrice) - meanTarget), 0);
      const spread = variance > 0.0001 ? Math.max(increment, covariance / variance) : Math.max(increment, Number(currentSettings?.ceiling_price || 0) - Number(currentSettings?.floor_price || 0));
      floorPrice = meanTarget - spread * meanWeight;
      ceilingPrice = floorPrice + spread;
    }
    floorPrice = Math.max(increment, snapIncrement(floorPrice, increment));
    ceilingPrice = Math.max(floorPrice, snapIncrement(ceilingPrice, increment));
    const candidate = { ...currentSettings, band_mode: bandMode, row_exponent: rowExponent, edge_strength: edgeStrength, floor_price: floorPrice, ceiling_price: ceilingPrice };
    const error = valid.reduce((sum, anchor) => {
      const predicted = calculateAmpModelPrice(candidate, anchor);
      return sum + (predicted - Number(anchor.targetPrice)) ** 2;
    }, 0);
    const complexityPenalty = Math.abs(rowExponent - Number(currentSettings?.row_exponent || 1.2)) * 0.1 + Math.abs(edgeStrength - Number(currentSettings?.edge_strength || 0.65)) * 0.1;
    const score = error + complexityPenalty;
    if (!best || score < best.score) best = { score, settings: candidate };
  })));
  return best?.settings || currentSettings;
};

export const applyAmpPricingModelToSeats = ({ seats = [], day = 'saturday', model, overrides = {}, multiplier = 1 }) => {
  if (!model?.enabled) return seats.map((seat) => ({ ...seat, effective_price: Math.max(1, Math.round(Number(seat.price ?? seat.source_price ?? 0) * multiplier)) }));
  const normalizedDay = day === 'sunday' ? 'sunday' : 'saturday';
  const modelDay = model.match_days ? 'saturday' : normalizedDay;
  const sections = model.days?.[modelDay]?.sections || {};
  const dayOverrides = overrides?.[modelDay] || {};
  const topology = new Map();
  seats.forEach((seat, sourceIndex) => {
    const section = String(seat.section || '');
    const row = String(seat.row || '');
    if (!topology.has(section)) topology.set(section, { rows: [], seats: new Map() });
    const sectionData = topology.get(section);
    if (!sectionData.seats.has(row)) {
      sectionData.rows.push(row);
      sectionData.seats.set(row, []);
    }
    sectionData.seats.get(row).push({ seat, sourceIndex });
  });
  const output = new Array(seats.length);
  topology.forEach((sectionData, sectionName) => {
    const baseSettings = sections[sectionName];
    const sectionOverride = dayOverrides[sectionName] || {};
    const settings = baseSettings ? {
      ...baseSettings,
      floor_price: snapIncrement(Number(sectionOverride.min ?? baseSettings.floor_price) * multiplier, baseSettings.round_to),
      ceiling_price: snapIncrement(Number(sectionOverride.max ?? baseSettings.ceiling_price) * multiplier, baseSettings.round_to),
      floor_middle_delta: Number(baseSettings.floor_middle_delta || 0) * multiplier,
    } : null;
    const rowOverrides = sectionOverride.rows || {};
    sectionData.rows.forEach((rowName, rowIndex) => {
      const rowSeats = sectionData.seats.get(rowName) || [];
      rowSeats.forEach(({ seat, sourceIndex }, seatIndex) => {
        let effective = settings
          ? calculateAmpModelPrice(settings, { rowIndex, rowCount: sectionData.rows.length, seatIndex, seatCount: rowSeats.length })
          : Math.max(1, Math.round(Number(seat.price ?? seat.source_price ?? 0) * multiplier));
        if (rowOverrides[rowName] != null) {
          effective = Math.max(1, Math.round(Number(rowOverrides[rowName]) * multiplier));
          if (settings) effective = snapIncrement(effective, settings.round_to);
        }
        output[sourceIndex] = {
          ...seat,
          source_price: Number(seat.source_price ?? seat.price ?? 0),
          effective_price: Math.round(effective),
          row_index: rowIndex,
          row_count: sectionData.rows.length,
          seat_index: seatIndex,
          seat_count: rowSeats.length,
          model_day: modelDay,
          model_enabled: Boolean(settings),
        };
      });
    });
  });
  const ranges = new Map();
  output.filter(Boolean).forEach((seat) => {
    const current = ranges.get(seat.section) || { min: Infinity, max: -Infinity };
    current.min = Math.min(current.min, seat.effective_price);
    current.max = Math.max(current.max, seat.effective_price);
    ranges.set(seat.section, current);
  });
  return output.filter(Boolean).map((seat) => {
    const range = ranges.get(seat.section);
    return { ...seat, heat: range.max <= range.min ? 1 : (seat.effective_price - range.min) / (range.max - range.min) };
  });
};

export const ampHeatColor = (heat, alpha = 1) => {
  const value = clamp(Number(heat || 0), 0, 1);
  const hue = 210 - value * 205;
  const lightness = 88 - value * 35;
  return `hsla(${hue}, 78%, ${lightness}%, ${alpha})`;
};
